import { createContext, useEffect, useState } from "react";
import ProfileService from "../services/ProfileService";

export const AuthContext = createContext();

const safeJsonParse = (value) => {
  try {
    return value && value !== "undefined" ? JSON.parse(value) : null;
  } catch (e) {
    return null;
  }
};

export const AuthContextProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [fullName, setFullName] = useState(() =>
    safeJsonParse(localStorage.getItem("currentUser"))
  );
  const [emailInfo, setEmailInfo] = useState(() =>
    safeJsonParse(localStorage.getItem("emailInfo"))
  );
  const [cnic, setCnic] = useState(() =>
    safeJsonParse(localStorage.getItem("cnic"))
  );

  const [profileImage, setProfileImage] = useState(() =>
    safeJsonParse(sessionStorage.getItem("profileImage")) ||
    "/images/dummy.png"
  );
  const [data, setData] = useState(() =>
    safeJsonParse(sessionStorage.getItem("data"))
  );
  const [lastLogin, setLastLogin] = useState(() =>
    safeJsonParse(localStorage.getItem("lastLogin"))
  );

  const [otpExpiry, setOtpExpiry] = useState(0);
  const [forgetOtpExpiry, setForgetOtpExpiry] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState(() => safeJsonParse(sessionStorage.getItem("number")))
  const [newStudent, setNewStudent] = useState(() => safeJsonParse(sessionStorage.getItem("newstudent")));
  const [emailVerify, setEmailVerify] = useState(() => safeJsonParse(localStorage.getItem("emailVerify")));
  const [phoneVerify, setPhoneVerify] = useState(() => safeJsonParse(localStorage.getItem("phoneVerify")));
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!token) return;

    let logoutTimer;

    const resetTimer = () => {
      clearTimeout(logoutTimer);
      logoutTimer = setTimeout(() => {
        logout();
        window.location.href = "/login";
      }, 40 * 60 * 1000);
    };

    // Events that will reset the inactivity timer
    const activityEvents = ["mousemove", "mousedown", "keypress", "touchstart", "scroll"];

    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    resetTimer(); // Start the timer

    return () => {
      clearTimeout(logoutTimer);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [token]);
  useEffect(() => {
    const loginTime = localStorage.getItem("loginTimestamp");
    const now = Date.now();
    const MAX_IDLE_TIME = 40 * 60 * 1000;

    const isLoggedOut = localStorage.getItem("loggedOut") === "true";

    if (!loginTime || isLoggedOut) {
      setLoading(false);
      return;
    }

    const timeDiff = now - parseInt(loginTime, 10);

    if (timeDiff > MAX_IDLE_TIME) {
      logout();
      window.location.href = "/login";
    } else {
      setLoading(false); // ✅ Valid session, now let UI load
    }
  }, []);



  const login = (accesstoken, user, email, phone, cnic, profile_image, lastLogin, new_student, email_verify, phone_verify) => {
    setToken(accesstoken);
    setFullName(user);
    setEmailInfo(email);
    setCnic(cnic);
    setLastLogin(lastLogin);
    setProfileImage(profile_image);
    setNewStudent(new_student)
    setEmailVerify(email_verify);
    setPhoneVerify(phone_verify);
    setPhoneNumber(phone)

    localStorage.setItem("token", accesstoken);
    localStorage.setItem("currentUser", JSON.stringify(user));
    localStorage.setItem("emailInfo", JSON.stringify(email));
    localStorage.setItem("cnic", JSON.stringify(cnic));
    localStorage.setItem("lastLogin", JSON.stringify(lastLogin));
    sessionStorage.setItem("profileImage", JSON.stringify(profile_image || ""));
    sessionStorage.setItem("newstudent", JSON.stringify(new_student || ""));
    localStorage.setItem("emailVerify", JSON.stringify(email_verify || ""));
    localStorage.setItem("phoneVerify", JSON.stringify(phone_verify || ""));
    sessionStorage.setItem("number", JSON.stringify(phone || ""));
    localStorage.removeItem("loggedOut");
    localStorage.setItem("loginTimestamp", Date.now().toString());

  };

  const flags = (value) => {
    setData(value);
    sessionStorage.setItem("data", JSON.stringify(value));
  };

  const updateProfileImage = (url) => {
    setProfileImage(url);
    sessionStorage.setItem("profileImage", JSON.stringify(url || ""));
  };

  const logout = () => {
    sessionStorage.clear();
    localStorage.clear();
    localStorage.setItem("loggedOut", "true");

    setToken(null);
    setFullName(null);
    setEmailInfo(null);
    setProfileImage(null);
    setCnic(null);
    setData(null);
    setLastLogin(null);
    setEmailVerify(null);
    setPhoneVerify(null);
  };



  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUser = safeJsonParse(sessionStorage.getItem("currentUser"));
    const savedEmail = safeJsonParse(localStorage.getItem("emailInfo"));
    const savedProfile = safeJsonParse(sessionStorage.getItem("profileImage"));
    const savedCnic = safeJsonParse(sessionStorage.getItem("cnic"));
    const savedLastLogin = safeJsonParse(localStorage.getItem("lastLogin"));
    const savedDataFlag = safeJsonParse(sessionStorage.getItem("data"));

    if (savedToken) setToken(savedToken);
    if (savedUser) setFullName(savedUser);
    if (savedEmail) setEmailInfo(savedEmail);
    if (savedProfile) setProfileImage(savedProfile);
    if (savedCnic) setCnic(savedCnic);
    if (savedLastLogin) setLastLogin(savedLastLogin);
    if (savedDataFlag !== null) setData(savedDataFlag);

    setLoading(false);
  }, []);


  useEffect(() => {
    if (token) {
      ProfileService.getStudentInfo()
        .then((response) => {
          const profilePhotoUrl = response.data?.data?.profile_photo_url;
          if (profilePhotoUrl) {
            updateProfileImage(profilePhotoUrl);
          }
        })
        .catch((error) => {
          // console.error("Failed to refresh profile image:", error);
        });
    }
  }, [token]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "logout") {
        logout();
        window.location.href = "/login";
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        fullName,
        emailInfo,
        profileImage,
        login,
        cnic,
        logout,
        updateProfileImage,
        lastLogin,
        flags,
        data,
        setToken,
        loading,
        newStudent,
        emailVerify,
        phoneVerify,
        setNewStudent,
        phoneNumber,
        otpExpiry,
        setOtpExpiry,
        forgetOtpExpiry,
        setForgetOtpExpiry

      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
